// apps/share-web/src/player-view/chart-fullscreen.ts
//
// Полноэкранное чтение чарта — общий СЛОЙ для аудио-плеера и страницы слов.
// Words page v2 (спека docs/superpowers/specs/2026-09-14-words-page-v2-design.md):
//
//   ряд 1     ✕ · имя (+ мета) · «2 / 8» · [чужая кнопка Live] · ⚙
//   лента     секции песни (только когда есть секции; лайв / ведение по темпу)
//   .cw-body  лист (.cw-pane) + парящие ‹ › + пилюля ▶ ведения по темпу
//   транспорт ряд Play/позиция — только у аудио-плеера (deps.transport)
//   лист настроек ⚙ — скрим + нижний лист (chart/sheet-settings.ts)
//
// Слой — ПРОВОДКА. Всё, что можно посчитать без DOM, живёт в chart/*: модель
// размера (chart-font), настройки читателя (reader-prefs — владелец, не слой),
// лента (section-strip), лист настроек (sheet-settings), подбор «вся песня на
// экране» (chart-fit), локальные часы (local-clock). Слой их монтирует и
// связывает; своих правил у него нет.
//
// ⚠️ Слой НЕ хранит настройки. Он получает начальные значения от владельца
// (`initial*`) и сообщает ему о каждом изменении (`on*Change`); владелец пишет
// их в reader-prefs и подаёт снова при следующем открытии. Так «зум не
// сохраняется» (баг 14.09) починен на стороне владельца, а слой остаётся без
// памяти и пересоздаётся на каждое открытие, как и раньше.

import { leadSeconds } from '@bandway/chordpro';
import { renderChart } from '../chart/chart-render.js';
import {
  createChartFollow,
  domViewport,
  type ChartFollow,
  type ChartFollowDeps,
  type SectionSec,
  type PositionSource,
} from '../chart/chart-follow.js';
import { iconButton } from '../chart/chart-buttons.js';
import { clampShift, clampColumns } from '../chart/chart-controls.js';
import { clampFontPct, fontScale } from '../chart/chart-font.js';
import { READER_COLUMNS_MAX, type ReaderTheme } from '../chart/reader-prefs.js';
import { mountSectionStrip, type SectionStripHandle } from '../chart/section-strip.js';
import { mountSheetSettings, type SheetSettingsHandle } from '../chart/sheet-settings.js';
import { sheetLyrics } from '../chart/lyrics-text.js';
import { copyText } from '../ui/copy-text.js';
import { planFit, type FitPlan } from '../chart/chart-fit.js';
import { bindEscape } from '../chart/chart-escape.js';
import {
  mountChartTransport,
  type ChartTransportDeps,
  type ChartTransportHandle,
} from './chart-transport.js';
import { t } from '../ui/i18n.js';

/** Сосед по сетлисту — ИМЯ, и только оно; чем открыть — знание вызывающего. */
export interface SetlistEntry {
  songName: string | null;
}

/** Локальное «ведение по темпу» глазами слоя: пилюля ▶/❚❚ внизу листа. */
export interface FollowPillDeps {
  isPlaying(): boolean;
  toggle(): void;
  /** Подпись: «0:42 · Verse 2». Считает владелец — у него часы и расписание. */
  label(): string;
}

export interface ChartFullscreenDeps {
  text: string;
  links?: Record<string, string> | null;
  songName: string;
  songKey: string;
  /** Готовая строка «C · 72 bpm · 4/4». Пустая — строки не будет. */
  songMeta: string;
  bpm?: number;
  timeSignature?: string;
  sections: SectionSec[];
  setlist: SetlistEntry[];
  /** Место песни в списке; вне диапазона (−1) — «нас в этом сетлисте нет». */
  currentIndex: number;
  /** Оставлено для вызывающих; потолок колонок с v2 один на все экраны. */
  isPhone: boolean;
  position: PositionSource;
  onNavigate: (index: number) => void;
  onClose: () => void;

  // ── настройки читателя: владелец подаёт, слой сообщает ──
  initialShift?: number;
  onShiftChange?: (shift: number) => void;
  initialColumns?: number;
  onColumnsChange?: (columns: number) => void;
  initialFontPct?: number;
  onFontPctChange?: (pct: number) => void;
  theme?: ReaderTheme;
  onThemeChange?: (theme: ReaderTheme) => void;
  fit?: boolean;
  onFitChange?: (on: boolean) => void;
  follow?: boolean;
  /** У песни есть bpm и секции — ведение по темпу можно включить. */
  followAvailable?: boolean;
  onFollowChange?: (on: boolean) => void;
  showChords?: boolean;
  onShowChordsChange?: (on: boolean) => void;
  showLyrics?: boolean;
  onShowLyricsChange?: (on: boolean) => void;
  /** Тап по чипу секции — владелец может перезавести свои часы. */
  onJumpSection?: (sectionIdx: number) => void;

  createFollow?: (opts: ChartFollowDeps) => ChartFollow;
  /** Транспорт аудио-плеера. Нет поля — нет ряда (страница слов, тесты). */
  transport?: ChartTransportDeps;
  /** Пилюля ведения по темпу. Нет поля — пилюли нет. */
  followPill?: FollowPillDeps;
  /** Чужой узел в первом ряду (кнопка Live страницы слов) — перед ⚙. */
  badge?: HTMLElement;
  /** Шов для тестов: happy-dom не знает ResizeObserver. null — не наблюдать. */
  ResizeObserver?: (new (cb: () => void) => { observe(el: Element): void; disconnect(): void }) | null;
}

export interface ChartFullscreenHandle {
  tickOnce(): void;
  /** Пересчитать «вся песня на экране» (владелец сменил текст/режим снаружи). */
  refit(): void;
  destroy(): void;
}

const el = (tag: string, cls?: string, txt?: string): HTMLElement => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (txt != null) e.textContent = txt;
  return e;
};

/**
 * Слова листа в буфер. Запасной путь — скрытое поле и `execCommand('copy')`:
 * ссылку на слова команда проектора открывает и во встроенном браузере
 * мессенджера, где Clipboard API бывает запрещён (ревью).
 */
function copyLyricsOf(pane: HTMLElement): () => Promise<boolean | 'empty'> {
  return async () => {
    const lyrics = sheetLyrics(pane);
    if (!lyrics) return 'empty';
    const field = document.createElement('textarea');
    field.value = lyrics;
    field.setAttribute('readonly', '');
    field.setAttribute('aria-hidden', 'true');
    field.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(field);
    try {
      return await copyText(lyrics, field);
    } finally {
      field.remove();
    }
  };
}

export function mountChartFullscreen(
  host: HTMLElement,
  deps: ChartFullscreenDeps,
): ChartFullscreenHandle {
  let shift = clampShift(deps.initialShift);
  let columns = clampColumns(deps.initialColumns, READER_COLUMNS_MAX);
  let fontPct = clampFontPct(deps.initialFontPct);
  let theme: ReaderTheme = deps.theme ?? 'dark';
  let fit = deps.fit === true;
  let follow = deps.follow === true;
  let showChords = deps.showChords !== false;
  let showLyrics = deps.showLyrics !== false;
  const followAvailable = deps.followAvailable === true;

  const total = deps.setlist.length;
  const raw = Math.trunc(deps.currentIndex);
  const placed = Number.isFinite(raw) && raw >= 0 && raw < total;
  const idx = placed ? raw : -1;
  const many = placed && total > 1;

  const root = el('div', 'cw-fs');
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', deps.songName);
  root.dataset.theme = theme;

  // ── ряд 1: ✕ · кто играет (+ мета) · где мы · [Live] · ⚙
  const row1 = el('div', 'cw-fs-row1');
  const btnClose = iconButton({
    glyph: '✕', name: t('chart.exitFullscreen'), cls: 'cw-btn-close', size: 'lg',
    onClick: () => deps.onClose(),
  });
  row1.appendChild(btnClose);   // ПЕРВЫЙ: макет v2 — крестик слева, ⚙ справа
  const who = el('div', 'cw-fs-who');
  who.appendChild(el('div', 'cw-fs-name', deps.songName));
  if ((deps.songMeta ?? '').trim() !== '') who.appendChild(el('div', 'cw-fs-meta', deps.songMeta));
  row1.appendChild(who);
  if (many) row1.appendChild(el('div', 'cw-fs-pos', `${idx + 1} / ${total}`));
  if (deps.badge) row1.appendChild(deps.badge);
  const btnGear = iconButton({
    glyph: '⚙', name: t('chart.settings'), cls: 'cw-btn-settings', size: 'lg',
    onClick: () => { if (sheet.isOpen()) sheet.close(); else sheet.open(); },
  });
  row1.appendChild(btnGear);    // ПОСЛЕДНИЙ
  root.appendChild(row1);

  // ── лента структуры: только когда есть секции (лайв / ведение по темпу)
  const strip: SectionStripHandle | null = mountSectionStrip(root, {
    sections: deps.sections,
    onJump: (i) => {
      follow_.scrollToSection(i);
      deps.onJumpSection?.(i);
    },
  });

  // ── тело: лист + парящие кнопки + пилюля
  const body = el('div', 'cw-body');
  const pane = el('div', 'cw-pane');
  body.appendChild(pane);

  if (many) {
    const mkFab = (cls: string, glyph: string, at: number, label: string): HTMLElement => {
      const entry = deps.setlist[at];
      return iconButton({
        glyph, name: label, cls: `cw-fab ${cls}`, size: 'lg', shape: 'circle',
        disabled: entry == null,
        onClick: entry != null ? () => deps.onNavigate(at) : undefined,
      });
    };
    body.appendChild(mkFab('cw-fab-prev cw-btn-prev', '‹', idx - 1, t('chart.prev')));
    body.appendChild(mkFab('cw-fab-next cw-btn-next', '›', idx + 1, t('chart.next')));
  }

  let pill: HTMLElement | null = null;
  let pillIcon: HTMLElement | null = null;
  let pillText: HTMLElement | null = null;
  if (deps.followPill) {
    const fp = deps.followPill;
    pill = el('button', 'cw-pill');
    (pill as HTMLButtonElement).type = 'button';
    pillIcon = el('span', 'cw-pill-icon', '▶');
    pillText = el('span', 'cw-pill-text', fp.label());
    pill.append(pillIcon, pillText);
    pill.addEventListener('click', () => { fp.toggle(); syncPill(); });
    body.appendChild(pill);
  }
  const syncPill = (): void => {
    if (!deps.followPill || !pill || !pillIcon || !pillText) return;
    const playing = deps.followPill.isPlaying();
    pillIcon.textContent = playing ? '❚❚' : '▶';
    pill.classList.toggle('is-playing', playing);
    pill.setAttribute('aria-label', playing ? t('chart.followPause') : t('chart.followPlay'));
    pillText.textContent = deps.followPill.label();
  };
  syncPill();
  root.appendChild(body);

  // ── транспорт аудио-плеера — самый низ
  const transport: ChartTransportHandle | null =
    deps.transport ? mountChartTransport(root, deps.transport) : null;

  host.appendChild(root);

  // ── ведение
  const follow_ = (deps.createFollow ?? createChartFollow)({
    pane,
    sections: deps.sections,
    links: deps.links,
    position: deps.position,
    viewport: domViewport(pane),
    leadSec: leadSeconds(deps.bpm ?? 0, deps.timeSignature),
  });

  // ── лист: размер, колонки, видимость, «вся песня на экране»
  let fitPlan: FitPlan | null = null;
  const effectiveColumns = (): number => fitPlan?.columns ?? columns;
  const effectiveFontPct = (): number => fitPlan?.fontPct ?? fontPct;

  const applyFont = (): void => {
    pane.style.setProperty('--cw-fs-scale', String(fontScale(effectiveFontPct())));
  };
  const applyVisibility = (): void => {
    pane.classList.toggle('cw-hide-chords', !showChords);
    pane.classList.toggle('cw-hide-lyrics', !showLyrics);
  };

  const draw = (): void => {
    const hasWords = (deps.text ?? '').trim().length > 0;
    pane.textContent = '';
    if (hasWords) renderChart(pane, { text: deps.text, shift, columns: effectiveColumns() });
    else pane.appendChild(el('div', 'cw-empty', t('chart.empty')));
    follow_.invalidate();
  };

  /**
   * «Вся песня на экране»: подбор считает chart-fit (чистый), измеряет слой.
   * Кандидат применяется, меряется `scrollHeight ≤ clientHeight`; первый
   * поместившийся остаётся. Ни один — лист на настройках читателя и подсказка
   * в листе настроек.
   */
  const refit = (): void => {
    // Запас 50vh снизу листа (прокрутка при ведении) в режиме «вся песня» не
    // нужен и ломает мерку: scrollHeight с ним всегда больше экрана. Класс
    // снимает его (chart.css, `.cw-pane.cw-fit`) — замер живьём 14.09.
    pane.classList.toggle('cw-fit', fit);
    if (!fit || (deps.text ?? '').trim() === '') {
      if (fitPlan) { fitPlan = null; applyFont(); draw(); }
      sheet.setFitFits(true);
      return;
    }
    const fits = (cols: number, pct: number): boolean => {
      fitPlan = { columns: cols, fontPct: pct };
      applyFont();
      draw();
      // Нулевая высота — раскладки нет (тесты, скрытый слой): считаем, что
      // помещается, чтобы не крутить перебор впустую.
      return pane.clientHeight <= 0 || pane.scrollHeight <= pane.clientHeight;
    };
    const plan = planFit({ columns, fontPct, fits });
    fitPlan = plan;
    if (!plan) { applyFont(); draw(); }
    sheet.setFitFits(plan != null);
  };

  const sheet: SheetSettingsHandle = mountSheetSettings(root, {
    songKey: deps.songKey,
    shift, columns, fontPct, theme, fit, follow, followAvailable, showChords, showLyrics,
    onShift: (n) => { shift = n; deps.onShiftChange?.(n); draw(); },
    // B-261002 — слова этой песни для слайдов (сборщик листа, chart/lyrics-text.ts).
    // Песне без текста ряд не нужен вовсе: копировать нечего, а «не удалось»
    // соврало бы про буфер.
    onCopyLyrics: (deps.text ?? '').trim() === '' ? undefined : copyLyricsOf(pane),
    onColumns: (n) => {
      columns = n; deps.onColumnsChange?.(n);
      pane.scrollTop = 0;
      if (fit) refit(); else draw();
    },
    onFontPct: (n) => {
      fontPct = n; deps.onFontPctChange?.(n);
      if (fit) refit(); else { applyFont(); follow_.invalidate(); }
    },
    onTheme: (v) => { theme = v; root.dataset.theme = v; deps.onThemeChange?.(v); },
    onFit: (on) => { fit = on; deps.onFitChange?.(on); refit(); },
    onFollow: (on) => { follow = on; deps.onFollowChange?.(on); },
    onShowChords: (on) => { showChords = on; deps.onShowChordsChange?.(on); applyVisibility(); if (fit) refit(); else follow_.invalidate(); },
    onShowLyrics: (on) => { showLyrics = on; deps.onShowLyricsChange?.(on); applyVisibility(); if (fit) refit(); else follow_.invalidate(); },
  });
  // ⚙ подсвечена, пока хоть что-то не по умолчанию — читатель видит, что лист «настроен».
  const markGear = (): void => {
    const dirty = fontPct !== 0 || columns !== 1 || theme !== 'dark' || fit || follow || !showChords || !showLyrics;
    btnGear.classList.toggle('is-on', dirty);
  };
  for (const ev of ['bw-click', 'change', 'click'] as const) root.addEventListener(ev, () => markGear());

  for (const ev of ['wheel', 'touchstart', 'pointerdown'] as const) {
    pane.addEventListener(ev, () => follow_.handOff(), { passive: true });
  }

  const unbindEscape = bindEscape(() => true, () => {
    if (sheet.isOpen()) { sheet.close(); return; }
    deps.onClose();
  });

  const tickOnce = (): void => {
    follow_.tickOnce();
    const at = deps.position.now();
    if (at) transport?.sync(at.posSec, at.playing);
    strip?.sync(at ? at.posSec : null);
    syncPill();
  };

  let raf: number | null = null;
  const loop = (): void => {
    tickOnce();
    raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(loop) : null;
  };

  // Ресайз листа — пересчёт «вся песня на экране».
  const RO = deps.ResizeObserver === undefined
    ? (typeof globalThis.ResizeObserver === 'function' ? globalThis.ResizeObserver : null)
    : deps.ResizeObserver;
  let ro: { disconnect(): void } | null = null;
  if (RO) {
    try { const o = new RO(() => { if (fit) refit(); }); o.observe(pane); ro = o; } catch { ro = null; }
  }

  applyVisibility();
  applyFont();
  draw();
  if (fit) refit();
  markGear();
  tickOnce();
  if (typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(loop);

  return {
    tickOnce,
    refit,
    destroy() {
      if (raf != null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
      raf = null;
      try { ro?.disconnect(); } catch { /* no-op */ }
      transport?.destroy();
      strip?.destroy();
      sheet.destroy();
      unbindEscape();
      root.remove();
    },
  };
}
